import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LogOut, LayoutDashboard, FileText, Receipt, CreditCard, User } from "lucide-react";

/**
 * Customer portal layout (separate from admin/public).
 * Uses the `a1_customer` cookie. If not signed in, redirects to /customer/login.
 */
export default function CustomerLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/customer/me")
            .then((r) => { if (alive) { setMe(r.data.customer); setLoading(false); } })
            .catch(() => { if (alive) navigate(`/customer/login?from=${encodeURIComponent(location.pathname)}`); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const logout = async () => {
        try { await api.post("/customer/auth/logout"); } catch { /* noop */ }
        navigate("/customer/login");
    };

    if (loading) return null;

    const links = [
        { to: "/customer/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/customer/estimates", label: "Estimates", icon: FileText },
        { to: "/customer/invoices", label: "Invoices", icon: Receipt },
        { to: "/customer/payments", label: "Payments", icon: CreditCard },
        { to: "/customer/profile", label: "Profile", icon: User },
    ];

    return (
        <div className="min-h-screen a1-bg-base text-[var(--color-text)]">
            <header className="border-b" style={{ borderColor: "var(--color-line)", background: "var(--color-bg-alt)" }}>
                <div className="a1-container py-3 flex items-center justify-between">
                    <Link to="/customer/dashboard" className="flex items-center gap-2.5 no-underline">
                        <span className="serif font-bold text-base" style={{ color: "var(--color-text)" }}>
                            A1 Screening — Customer Portal
                        </span>
                    </Link>
                    {me ? (
                        <div className="flex items-center gap-3">
                            <span className="text-sm hidden sm:inline" style={{ color: "var(--color-text-muted)" }}>
                                {me.name || me.email || me.phone}
                            </span>
                            <button
                                onClick={logout}
                                className="a1-btn-ghost"
                                data-testid="customer-logout"
                            >
                                <LogOut size={14} /> Sign out
                            </button>
                        </div>
                    ) : null}
                </div>
            </header>
            <div className="a1-container py-8 grid lg:grid-cols-12 gap-8">
                <aside className="lg:col-span-3">
                    <nav className="flex lg:flex-col gap-1 overflow-x-auto">
                        {links.map((l) => {
                            const active = location.pathname.startsWith(l.to);
                            const Icon = l.icon;
                            return (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    className="px-3 py-2.5 rounded-md text-sm font-semibold uppercase tracking-wider no-underline inline-flex items-center gap-2 whitespace-nowrap"
                                    style={{
                                        background: active ? "var(--color-bg-card)" : "transparent",
                                        color: active ? "var(--color-accent)" : "var(--color-text)",
                                        border: `1px solid ${active ? "var(--color-line-2)" : "transparent"}`,
                                    }}
                                    data-testid={`customer-nav-${l.label.toLowerCase()}`}
                                >
                                    <Icon size={14} /> {l.label}
                                </Link>
                            );
                        })}
                    </nav>
                </aside>
                <main className="lg:col-span-9">
                    <Outlet context={{ me }} />
                </main>
            </div>
        </div>
    );
}
